import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";

import { Button, DataTable, SegmentedControl } from "../../shared";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatUtcTimestampWithLocal } from "../../../queries/regulator-portal/compliance/formatUtcTimestamp";
import { useReportingRuns } from "../../../queries/regulator-portal/compliance/useReportingRuns";
import { useStartReportingRun } from "../../../queries/regulator-portal/compliance/useStartReportingRun";
import { useRegulatorMe } from "../../../queries/regulator-portal/useRegulatorMe";
import type { ReportingPack } from "../../../queries/regulator-portal/regulatorMeClient";
import type {
  ReportingRun,
} from "../../../queries/regulator-portal/compliance/parseReportingRuns";
import type { ReportingRunListOrder } from "../../../queries/regulator-portal/compliance/reportingPaths";
import { buildRegulatorPortalReportPath } from "../regulatorPortalRoutes";
import { portalComplianceErrorMessage } from "./portalComplianceError";

import styles from "./Compliance.module.css";

const emptyValue = (value: string) => value || AppStrings.EMPTY_REASON;
const reportingPackLabels: Record<ReportingPack, string> = {
  gli: AppStrings.PAM_REGULATOR_PORTAL_PACK_GLI,
  gli_brazil: AppStrings.PAM_REGULATOR_PORTAL_PACK_BRAZIL,
  gli_peru: AppStrings.PAM_REGULATOR_PORTAL_PACK_PERU,
};

const RegulatorPortalReports: FC = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState<ReportingRunListOrder>("desc");
  const [referenceDate, setReferenceDate] = useState("");
  const [selectedPack, setSelectedPack] = useState<ReportingPack>("gli");
  const { data: regulatorMe } = useRegulatorMe();
  const runsQuery = useReportingRuns(order);
  const startRun = useStartReportingRun();
  const rows = runsQuery.data ?? [];
  const canStart = Boolean(referenceDate);
  const allowedReportingPacks = regulatorMe?.allowedReportingPacks ?? [];
  const showReportingPackSelector = allowedReportingPacks.length > 0;

  useEffect(() => {
    if (allowedReportingPacks.length === 0) return;
    const nextPack =
      (regulatorMe?.defaultReportingPack &&
      allowedReportingPacks.includes(regulatorMe.defaultReportingPack)
        ? regulatorMe.defaultReportingPack
        : allowedReportingPacks.includes("gli")
          ? "gli"
          : allowedReportingPacks[0]) ?? "gli";
    setSelectedPack(nextPack);
  }, [regulatorMe?.defaultReportingPack, allowedReportingPacks]);
  const loadError =
    runsQuery.isError && rows.length === 0
      ? portalComplianceErrorMessage(
          runsQuery.error,
          AppStrings.PAM_REGULATOR_PORTAL_REPORTS_LOAD_ERROR,
        )
      : null;

  const columns = useMemo(
    () => [
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_REPORT_REFERENCE_DATE,
        dataIndex: "referenceDate",
        key: "referenceDate",
        render: (value: string, record: ReportingRun) => (
          <button
            type="button"
            className={styles.rowLink}
            onClick={() => navigate(buildRegulatorPortalReportPath(record.runId))}
          >
            {emptyValue(value || record.runId)}
          </button>
        ),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_REPORT_PACK,
        dataIndex: "pack",
        key: "pack",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_REPORT_BRAND,
        dataIndex: "brand",
        key: "brand",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.STATUS,
        dataIndex: "status",
        key: "status",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_REPORT_LAST_MODIFIED,
        dataIndex: "lastModified",
        key: "lastModified",
        render: (value: string) =>
          formatUtcTimestampWithLocal(value) || AppStrings.EMPTY_REASON,
      },
    ],
    [navigate],
  );

  const handleOrderChange = useCallback((next: ReportingRunListOrder) => {
    setOrder(next);
  }, []);

  const handleStartReport = useCallback(async () => {
    if (!referenceDate) return;
    try {
      const runId = await startRun.mutateAsync({
        referenceDate,
        ...(showReportingPackSelector ? { pack: selectedPack } : {}),
      });
      message.success(AppStrings.PAM_REGULATOR_PORTAL_START_REPORT_SUCCESS);
      if (runId) {
        navigate(buildRegulatorPortalReportPath(runId));
      }
    } catch (error) {
      message.error(
        portalComplianceErrorMessage(
          error,
          AppStrings.PAM_REGULATOR_PORTAL_START_REPORT_ERROR,
        ),
      );
    }
  }, [
    navigate,
    referenceDate,
    selectedPack,
    showReportingPackSelector,
    startRun,
  ]);

  return (
    <section className={styles.page} data-testid="regulator-portal-reports">
      <div className={styles.header}>
        <h1 className={styles.title}>{AppStrings.PAM_REGULATOR_PORTAL_REPORTS}</h1>
        <div className={styles.headerActions}>
          <div className={styles.dateField}>
            <label
              className={styles.dateFieldLabel}
              htmlFor="regulator-portal-report-reference-date"
            >
              {AppStrings.PAM_REGULATOR_PORTAL_REPORT_REFERENCE_DATE}
            </label>
            <input
              id="regulator-portal-report-reference-date"
              data-testid="regulator-portal-report-reference-date"
              className={styles.dateInput}
              type="date"
              value={referenceDate}
              onChange={(event) => setReferenceDate(event.target.value)}
            />
          </div>
          {showReportingPackSelector ? (
            <div className={styles.dateField}>
              <label
                className={styles.dateFieldLabel}
                htmlFor="regulator-portal-report-pack"
              >
                {AppStrings.PAM_REGULATOR_PORTAL_REPORT_PACK}
              </label>
              <select
                id="regulator-portal-report-pack"
                data-testid="regulator-portal-report-pack"
                className={styles.dateInput}
                value={selectedPack}
                onChange={(event) =>
                  setSelectedPack(event.target.value as ReportingPack)
                }
              >
                {allowedReportingPacks.map((pack) => (
                  <option key={pack} value={pack}>
                    {reportingPackLabels[pack]}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <Button
            onClick={handleStartReport}
            loading={startRun.isPending}
            disabled={!canStart}
            data-testid="regulator-portal-start-report"
          >
            {AppStrings.PAM_REGULATOR_PORTAL_START_REPORT}
          </Button>
        </div>
      </div>
      <div
        className={styles.pastReports}
        data-testid="regulator-portal-past-reports"
      >
        <div className={styles.header}>
          <h2 className={styles.pastReportsTitle}>
            {AppStrings.PAM_REGULATOR_PORTAL_PAST_REPORTS}
          </h2>
          <SegmentedControl<ReportingRunListOrder>
            ariaLabel={AppStrings.SORT}
            data-testid="regulator-portal-reports-sort"
            value={order}
            onChange={handleOrderChange}
            items={[
              { id: "desc", label: AppStrings.PAM_REGULATOR_PORTAL_SORT_NEWEST },
              { id: "asc", label: AppStrings.PAM_REGULATOR_PORTAL_SORT_OLDEST },
            ]}
          />
        </div>
        {loadError ? (
          <p className={styles.message} role="alert">
            {loadError}
          </p>
        ) : null}
        {runsQuery.isLoading ? (
          <p className={styles.message}>{AppStrings.PAM_REGULATOR_PORTAL_LOADING}</p>
        ) : null}
        {!runsQuery.isLoading && !loadError && rows.length === 0 ? (
          <p className={styles.message}>
            {AppStrings.PAM_REGULATOR_PORTAL_REPORTS_EMPTY}
          </p>
        ) : null}
        {rows.length > 0 ? (
          <div className={styles.tableSection}>
            <DataTable
              rowKey="runId"
              columns={columns}
              dataSource={rows}
              pagination={false}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default RegulatorPortalReports;
